import { faSpinnerThird } from "@fullscript/pro-regular-svg-icons";
import { faAngleDown } from "@fullscript/pro-solid-svg-icons";
import type { ChangeEventHandler, HTMLProps, ReactNode } from "react";
import { useState } from "react";

import { FormFieldInformation } from "@aviary/components/FormFieldInformation";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { useFloatingLabelFocus } from "../FloatingLabelInput/useFloatingLabelFocus";

import * as styles from "./FloatingLabelSelect.styles";

interface Props extends HTMLProps<HTMLSelectElement> {
  /**
   * Unique field ID
   *
   * @required
   */
  id: string;
  /**
   * Label for the floating text input
   *
   * @required
   */
  label: string;
  /**
   * Callback from the HTML element on change
   */
  handleChange?: ChangeEventHandler<HTMLSelectElement>;
  /**
   * Adds padding if there is an icon present in the input
   *
   * @default false
   */
  hasIcon?: boolean;
  /**
   * Adds in a loading icon if the select is updating
   *
   * @default false
   */
  isLoading?: boolean;
  /**
   * Array of field-level errors that is passed into <FormFieldInformation />
   */
  errors?: ReactNode[];
  /**
   * Text shown below the input to describe it's function
   */
  descriptiveText?: string;
  /**
   * Specifies whether the input's background is white or gray05
   *
   * @default false
   */
  hasWhiteBackground?: boolean;
}

/**
 * @deprecated Use `SelectInput` from `@fullscript/aviary-web` instead.
 */
const FloatingLabelSelect = (props: Props) => {
  const {
    value,
    disabled = false,
    isLoading,
    id,
    label,
    handleChange,
    descriptiveText,
    hasIcon,
    name,
    onBlur,
    errors,
    hasWhiteBackground,
    required = false,
    ...rest
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const { isFocused, handleBlur, handleFocus } = useFloatingLabelFocus(onBlur);

  const hasErrors = (): boolean => {
    return !!errors && errors.length > 0;
  };

  type StyleElement = Partial<
    typeof styles.label & typeof styles.htmlSelect & typeof styles.rightIcon
  >;

  const conditionalVariationStyler = (element: StyleElement, useBase = true) => {
    return [
      useBase && element.base,
      isHovered && element.hovered,
      Boolean(value) && element.filled,
      isFocused && element.focused,
      !isFocused && hasErrors() && element.errorStyles,
      disabled && element.disabled,
    ];
  };

  const conditionalFillStyles = (elementStyle: StyleElement) => {
    return [
      elementStyle.base,
      isHovered && elementStyle.hovered,
      Boolean(value) && elementStyle.filled,
      isFocused && elementStyle.focused,
      !isFocused && hasErrors() && elementStyle.errorStyles,
      disabled && elementStyle.disabled,
      hasWhiteBackground && null,
    ];
  };

  const handleMouseEnter = () => {
    if (disabled) return;

    setIsHovered(true);
  };

  const handleMouseExit = () => {
    if (disabled) return;

    setIsHovered(false);
  };

  const renderSelectIcon = () => {
    return isLoading ? (
      <FontAwesomeIcon
        size="sm"
        spin
        icon={faSpinnerThird}
        css={conditionalVariationStyler(styles.rightIcon)}
      />
    ) : (
      <FontAwesomeIcon
        size="sm"
        icon={faAngleDown}
        css={conditionalVariationStyler(styles.rightIcon)}
      />
    );
  };

  const renderRequiredAsterisk = () => {
    if (required) {
      return (
        <span aria-hidden css={styles.requiredAsterisk}>
          *
        </span>
      );
    }
  };

  const renderFormFieldInformation = () => {
    if (disabled) return null;

    return (
      <FormFieldInformation
        id={id}
        disabled={disabled}
        errors={!isFocused ? errors : undefined}
        descriptiveText={descriptiveText}
      />
    );
  };

  return (
    <div
      css={hasIcon ? styles.hasIconStyles : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseExit}
    >
      <div className="legacy-floatingLabelSelect__wrapper" css={styles.floatingLabelWrapper}>
        <label
          className="legacy-floatingLabelSelect__label"
          css={[
            styles.label.base,
            conditionalFillStyles(styles.label),
            conditionalVariationStyler(styles.label, false),
          ]}
          htmlFor={id}
        >
          {label}
          {renderRequiredAsterisk()}
        </label>
        <select
          className="legacy-floatingLabelSelect__select"
          css={conditionalFillStyles(styles.htmlSelect)}
          id={id}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          name={name}
          required={required}
          aria-errormessage={`${id}Error`}
          aria-invalid={hasErrors()}
          {...rest}
        />
        {renderSelectIcon()}
      </div>
      {renderFormFieldInformation()}
    </div>
  );
};

export { FloatingLabelSelect };
